import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { visuallyHidden } from "@mui/utils";
import dayjs from "dayjs";
import { ClioService, FilingReportItem, OpenAPI } from "../../../api";
import FilterableSelect from "../../../components/basic/FilterableSelect";
import { FILING_REPORT_STATUS_OPTIONS } from "./filingReportStatusOptions";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const STATUS_FILTER_ALL = "";
const STATUS_FILTER_NONE = "__none__";
const TASK_FILTER_ALL = "";

type SortableColumn =
  | "taskName"
  | "caseName"
  | "dueDate"
  | "status"
  | "statusUpdatedAt"
  | "caseTeam";

type SortDirection = "asc" | "desc";

const HEAD_CELLS: { id: SortableColumn; label: string }[] = [
  { id: "taskName", label: "Task" },
  { id: "caseName", label: "Case Name" },
  { id: "dueDate", label: "Due Date" },
  { id: "status", label: "Status" },
  { id: "statusUpdatedAt", label: "Status Updated" },
  { id: "caseTeam", label: "Case Team" },
];

function getSortValue(row: FilingReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "caseTeam":
      return row.caseTeam?.toLowerCase() ?? "";
    case "status":
      return row.status?.toLowerCase() ?? "";
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    case "statusUpdatedAt":
      return row.statusUpdatedAt ? dayjs(row.statusUpdatedAt).valueOf() : 0;
    default:
      return "";
  }
}

function compareRows(
  a: FilingReportItem,
  b: FilingReportItem,
  orderBy: SortableColumn,
  order: SortDirection
) {
  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  if (aValue < bValue) {
    return order === "asc" ? -1 : 1;
  }
  if (aValue > bValue) {
    return order === "asc" ? 1 : -1;
  }
  return 0;
}

export default function FilingReport() {
  const [reportData, setReportData] = useState<FilingReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [taskFilter, setTaskFilter] = useState(TASK_FILTER_ALL);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortDirection>("asc");
  const [orderBy, setOrderBy] = useState<SortableColumn>("dueDate");

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioFilingReport()
      .then(setReportData)
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading filing report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const taskOptions = useMemo(
    () =>
      Array.from(
        new Set(reportData.map((row) => row.taskName).filter((name): name is string => !!name))
      )
        .sort((a, b) => a.localeCompare(b))
        .map((task) => ({ value: task, label: task })),
    [reportData]
  );

  const filteredData = useMemo(() => {
    let data = reportData;

    if (taskFilter) {
      data = data.filter((row) => row.taskName === taskFilter);
    }

    if (statusFilter === STATUS_FILTER_NONE) {
      data = data.filter((row) => !row.status);
    } else if (statusFilter) {
      data = data.filter((row) => row.status === statusFilter);
    }

    if (!searchTerm.trim()) {
      return data;
    }

    const lower = searchTerm.toLowerCase();
    return data.filter(
      (row) =>
        row.caseName?.toLowerCase().includes(lower) ||
        row.caseTeam?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.status?.toLowerCase().includes(lower)
    );
  }, [reportData, searchTerm, statusFilter, taskFilter]);

  const sortedData = useMemo(
    () =>
      [...filteredData].sort((a, b) => compareRows(a, b, orderBy, order)),
    [filteredData, order, orderBy]
  );

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (column: SortableColumn) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0);
  };

  const handleStatusChange = async (row: FilingReportItem, status: string) => {
    const nextStatus = status || null;
    const previousStatus = row.status ?? null;
    const previousUpdatedAt = row.statusUpdatedAt ?? null;
    const previousUpdatedByName = row.statusUpdatedByName ?? null;
    const updatedAt = new Date().toISOString();

    setReportData((prev) =>
      prev.map((item) =>
        item.taskId === row.taskId
          ? {
              ...item,
              status: nextStatus,
              statusUpdatedAt: updatedAt,
            }
          : item
      )
    );

    setSavingTaskId(row.taskId);
    try {
      await ClioService.postApiClioSaveFilingReportStatuses([
        {
          taskId: row.taskId,
          matterId: row.matterId,
          status: nextStatus,
        },
      ]);
    } catch {
      setReportData((prev) =>
        prev.map((item) =>
          item.taskId === row.taskId
            ? {
                ...item,
                status: previousStatus,
                statusUpdatedAt: previousUpdatedAt,
                statusUpdatedByName: previousUpdatedByName,
              }
            : item
        )
      );
      MySwal.fire({
        icon: "error",
        title: <p>Error saving status.</p>,
      });
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/filingReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taskName: taskFilter || null,
          statusFilter: statusFilter || null,
          searchTerm: searchTerm.trim() || null,
        }),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `FilingReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading filing report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  const isOverdue = (dueDate?: string | null) => {
    if (!dueDate) return false;
    return dayjs(dueDate).isBefore(dayjs(), "day");
  };

  const formatStatusUpdatedAt = (value?: string | null) => {
    if (!value) return "—";
    return dayjs(value).format("MM/DD/YYYY h:mm A");
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Filing Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pending tasks assigned to Narissa Estrada with filing status tracking.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FilterableSelect
            label="Task"
            value={taskFilter || TASK_FILTER_ALL}
            options={taskOptions}
            onChange={(value) => {
              setTaskFilter(
                value === undefined || value === TASK_FILTER_ALL
                  ? TASK_FILTER_ALL
                  : String(value)
              );
              setPage(0);
            }}
            allOptionLabel="All tasks"
            allOptionValue={TASK_FILTER_ALL}
            searchPlaceholder="Type to filter tasks..."
            minWidth={280}
          />
          <FormControl size="small" sx={{ width: 280 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
              <MenuItem value={STATUS_FILTER_NONE}>No status</MenuItem>
              {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder="Search cases, team, or tasks..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 320 }, flexGrow: { sm: 1 }, minWidth: 220 }}
          />
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={18} /> : <FileDownloadIcon />}
            onClick={handleDownload}
            disabled={downloading || loading}
          >
            Download
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {HEAD_CELLS.map((headCell) => (
                <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={HEAD_CELLS.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEAD_CELLS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No tasks match the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => (
                <TableRow key={row.taskId} hover>
                  <TableCell sx={{ maxWidth: 220 }}>{row.taskName}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{row.caseName}</TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: isOverdue(row.dueDate) ? "error.main" : "inherit",
                      fontWeight: isOverdue(row.dueDate) ? 600 : 400,
                    }}
                  >
                    {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <FormControl size="small" fullWidth disabled={savingTaskId === row.taskId}>
                      <Select
                        displayEmpty
                        value={row.status ?? ""}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              Select status
                            </Typography>
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8125rem" }}>
                    {formatStatusUpdatedAt(row.statusUpdatedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-line", fontSize: "0.8125rem" }}>
                    {row.caseTeam}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}
